import React, { useCallback, useContext, useEffect, useState, createContext } from 'react';
import { AuthService } from '../services/api/auth/AuthService';
import { CustomAlert } from '../components/shared/VAlert';
import { IUser } from '../services/api/users/UserServices';

interface IAuthContextData {
  isAuthenticated: boolean;
  logout: () => void;
  login: (login: string, senha: string) => Promise<string | void>;
  userData: IUser;
  reloadUserData: () => Promise<void>; 
}

const AuthContext = createContext<IAuthContextData | undefined>(undefined);

interface IAuthProviderProps {
  children: React.ReactNode;
}

const SESSION_STORAGE_KEY_ACCESS_TOKEN = 'access_token';
const LOCAL_STORAGE_USER_TOKEN = 'user_token';
const LOCAL_STORAGE_KEY_REFRESH_TOKEN = 'refresh_token';

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {

  const [authenticated, setAuthenticated] = useState<boolean>();
  const [userData, setUserData] = useState<IUser>({
    login: '',
    senha: '',
    nome: '',
    email: '',
    nivel: '',
    ativo: false,
    main_user: false,
    id_empresa: '',
    permissoes: '',
    foto: '',
    register: '',
    ramal: '',
    mobile_phone: '',
    role: '',
    sector: '',
    uuid: ''
  });

  const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlertInfo({ open: false, message: '', severity: 'info' });
    setTimeout(() => {
      setAlertInfo({ open: true, message, severity });
    }, 100);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem(LOCAL_STORAGE_USER_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN);
    sessionStorage.removeItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);
    setAuthenticated(false);
    setUserData({
      login: '',
      senha: '',
      nome: '',
      email: '',
      nivel: '',
      ativo: false,
      main_user: false,
      id_empresa: '',
      permissoes: '',
      foto: '',
      register: '',
      ramal: '',
      mobile_phone: '',
      role: '',
      sector: '',
      uuid: ''
    });
  };

  const fetchData = async () => {
    const access_token = sessionStorage.getItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);
    const refresh_token = localStorage.getItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN);

    if (access_token === null) {
      if (refresh_token === null) {
        clearAuthData();
      } else {
        const access = await AuthService.refresh();
        if (access instanceof Error || access === false) {
          clearAuthData();
        } else {
          handleUserToken();
        }
      }
    } else {
      setAuthenticated(true);
      handleUserToken();
    }
  };

  const handleUserToken = async () => {
    const user_token = localStorage.getItem(LOCAL_STORAGE_USER_TOKEN);
    if (user_token !== null) {
      const user = await AuthService.decrypt(user_token);
      if (user instanceof Error) {
        clearAuthData();
      } else {
        const userData = JSON.parse(user);
        setUserData(userData);
        setAuthenticated(true);
      }
    } else {
      clearAuthData();
    }
  };

  const reloadUserData = useCallback(async () => {
    await handleUserToken();
  }, []);

  const handleLogin = useCallback(async (user: string, password: string) => {
    const result = await AuthService.login(user, password);
    if (result instanceof Error) {
      showAlert('Falha ao fazer login, revise sua senha e nome de usuário.', 'error');
    } else {
      setAuthenticated(true);
      const user_token = localStorage.getItem(LOCAL_STORAGE_USER_TOKEN);
      if (user_token !== null) {
        const user = await AuthService.decrypt(user_token);
        if (user instanceof Error) {
          setAuthenticated(false);
        } else {
          const userObj = JSON.parse(user);
          setUserData(userObj);
        }
      }
    }
  }, []);

  const handleLogout = useCallback(async () => {
    const access_token = sessionStorage.getItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);
    const refresh_token = localStorage.getItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN);

    if (access_token !== null && refresh_token !== null) {

      try {
        await AuthService.logout(refresh_token);
        await AuthService.logout(access_token);
      } catch (error) {
        return 'erro durante o logout';
      }
    }

    sessionStorage.removeItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_USER_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN);
    setAuthenticated(false);
  }, []);

  const isAuthenticated = (authenticated == undefined ? false : authenticated);

  return (
    <>
      <AuthContext.Provider value={{
        isAuthenticated,
        login: handleLogin,
        logout: handleLogout,
        userData,
        reloadUserData
      }}>
        {children}
      </AuthContext.Provider>
      {alertInfo.open &&
        <CustomAlert
          message={alertInfo.message}
          severity={alertInfo.severity}
        />
      }
    </>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};